/**
 * FinalNightPage ✦ — iter200b
 * ─────────────────────────────────────────────────────────────
 * The archival surface for "The Final Night ✦".
 *
 * Constitutional anchors (from the iter200b brief):
 *
 *   • The Chronicle is part of the evening's emotional arc —
 *     not a post-event export utility, dashboard attachment,
 *     admin report, or downloadable afterthought.
 *
 *   • It should NEVER feel "generated afterward from uploads."
 *     It must feel "present during the evening as a quiet
 *     archival layer." The room is already writing it.
 *
 *   • This page is the editorial surface that opens when a
 *     guest or host taps "The Final Night ✦" inside Booth OS
 *     or the Leavers Night dashboard.
 *
 *   • Forbidden drift: NO AI scrapbook · NO design tool · NO
 *     social newspaper · NO meme builder · NO Canva editor.
 *     Restraint IS the product.
 *
 * Three calm states, keyed to where the evening is:
 *
 *   before — "The Chronicle will begin forming as the room fills ✦"
 *   during — "The Chronicle is quietly gathering the evening ✦"
 *   after  — "Compose The Final Night ✦"
 *
 * State derivation is deliberately atmospheric (not analytic):
 * the page reads the existing engagement signals already used by
 * Booth OS (uploads, marks, words) plus event date if exposed.
 * No new backend. No progress bars. No percentages. No
 * completion states. No action that promises something the
 * Chronicle composer cannot yet deliver.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Walnut · gold · ivory archival palette — matches the editorial
// register of GuestAccessPage and the Booth OS preserve section.
const INK = '#1f1409';
const INK_SOFT = 'rgba(31,20,9,0.72)';
const INK_FAINT = 'rgba(31,20,9,0.5)';
const PARCHMENT = '#f5efdf';
const GOLD = '#a4824a';
const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

// Editorial copy is LOCKED per the iter200b brief. Do not paraphrase.
const COPY = {
  eyebrow: 'PRESERVE THE NIGHT ✦',
  title: 'The Final Night ✦',
  supporting: 'Photos, voices, handwritten marks, final dancefloor moments and fragments of the year group — gathered into one archival edition after the evening settles.',
  states: {
    before: 'The Chronicle will begin forming as the room fills ✦',
    during: 'The Chronicle is quietly gathering the evening ✦',
    after:  'Compose The Final Night ✦',
  },
};

// Read whichever event-date field the backend happens to carry; the
// helper has to be defensive because the eventdrop catalogue stores
// dates under several names across editions.
const eventTimestamp = (event) => {
  if (!event) return null;
  const candidates = [
    event.event_date, event.event_at, event.starts_at,
    event.start_at, event.start_date,
  ];
  for (const c of candidates) {
    if (!c) continue;
    const t = Date.parse(c);
    if (!Number.isNaN(t)) return t;
  }
  return null;
};

// Resolve which of the three editorial states the page is in. The
// heuristic is deliberately patient — if no signal is available we
// fall through to "during", which is the most emotionally honest
// default for a Chronicle that exists *while* the evening unfolds.
const resolveChronicleState = ({ event, signals }) => {
  const ts = eventTimestamp(event);
  const now = Date.now();
  const hasActivity = signals && (
    (signals.uploads || 0) +
    (signals.marks || 0) +
    (signals.words || 0)
  ) > 0;
  if (ts) {
    // After: event date is more than 6 hours in the past — the room
    // has settled. Composition can begin.
    if (now - ts > 6 * 3600 * 1000) return 'after';
    // Before: event date is more than 8 hours in the future and
    // nothing has landed yet — the Chronicle has not begun.
    if (ts - now > 8 * 3600 * 1000 && !hasActivity) return 'before';
  } else if (!hasActivity) {
    return 'before';
  }
  return 'during';
};

const ChronicleFragment = ({ count, singular, plural }) => {
  if (!count || count < 1) return null;
  return (
    <li
      data-testid={`final-night-fragment-${singular.replace(/\s+/g, '-')}`}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 14,
        padding: '14px 0',
        borderBottom: '1px solid rgba(31,20,9,0.08)',
      }}
    >
      <span style={{
        fontFamily: FONT_DISPLAY, fontSize: 28, color: INK,
        letterSpacing: '-0.01em', minWidth: 56,
      }}>{count}</span>
      <span style={{
        fontFamily: FONT_DISPLAY, fontStyle: 'italic',
        fontSize: 17, color: INK_SOFT, lineHeight: 1.4,
      }}>
        {count === 1 ? singular : plural}
      </span>
    </li>
  );
};

const FinalNightPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [signals, setSignals] = useState({ uploads: 0, marks: 0, words: 0 });
  const [loading, setLoading] = useState(true);

  // Resolve the event once, then quietly probe the three signal
  // surfaces. We deliberately swallow all errors — this page must
  // never display an operational state. If a signal can't be read
  // the editorial register simply shows fewer fragments.
  useEffect(() => {
    if (!slug) return undefined;
    let aborted = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (aborted) return;
        const ev = r.data;
        setEvent(ev);
        const eventId = ev?.event_id;
        const [photos, marks, words] = await Promise.all([
          eventId
            ? axios.get(`${API}/events/${eventId}/photos?limit=1`).catch(() => null)
            : Promise.resolve(null),
          axios.get(`${API}/remember-us/events/${slug}/meta`).catch(() => null),
          axios.get(`${API}/one-word/event/${slug}/summary`).catch(() => null),
        ]);
        if (aborted) return;
        setSignals({
          uploads: Array.isArray(photos?.data) ? photos.data.length : (photos?.data?.total || 0),
          marks: marks?.data?.total || 0,
          words: words?.data?.total || words?.data?.count || 0,
        });
      } catch {
        /* page renders the calm "before" state */
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [slug]);

  const state = useMemo(
    () => resolveChronicleState({ event, signals }),
    [event, signals],
  );

  return (
    <div
      data-testid="final-night-page"
      data-chronicle-state={state}
      style={{
        minHeight: '100vh',
        background: PARCHMENT,
        color: INK,
        fontFamily: FONT_BODY,
        padding: '24px 22px 64px',
      }}
    >
      <BackButton
        fallbackHref={`/event/${slug}`}
        label="Back"
        variant="on-light"
        testId="final-night-back-btn"
      />

      <main style={{ maxWidth: 560, margin: '32px auto 0' }}>
        <p
          data-testid="final-night-eyebrow"
          style={{
            margin: 0, fontFamily: FONT_BODY, fontSize: 11,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: INK_FAINT,
          }}
        >
          {COPY.eyebrow}
        </p>
        <h1
          data-testid="final-night-title"
          style={{
            margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontWeight: 500,
            fontSize: 'clamp(36px, 6vw, 52px)', lineHeight: 1.05,
            letterSpacing: '-0.015em', color: INK,
          }}
        >
          {COPY.title}
        </h1>

        {/* State-aware editorial line. This is the single emotional
            CTA the page carries — no buttons, no controls, no
            promises the Chronicle composer cannot yet keep. */}
        <p
          data-testid="final-night-state-line"
          style={{
            margin: '24px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 'clamp(19px, 2.2vw, 24px)', lineHeight: 1.45,
            color: GOLD, letterSpacing: '0.01em',
          }}
        >
          {COPY.states[state]}
        </p>

        <p
          data-testid="final-night-supporting"
          style={{
            margin: '28px 0 0', fontFamily: FONT_BODY,
            fontSize: 14.5, lineHeight: 1.65, color: INK_SOFT,
          }}
        >
          {COPY.supporting}
        </p>

        {/* Quiet fragment ledger — only what the room has already
            given. Never zero-states, never "voices captured: 0".
            If nothing has landed yet, the ledger simply doesn't
            render — the supporting line carries the page alone. */}
        {!loading && state !== 'before' && (signals.uploads + signals.marks + signals.words) > 0 && (
          <ul
            data-testid="final-night-ledger"
            style={{
              listStyle: 'none', padding: 0, margin: '40px 0 0',
              borderTop: '1px solid rgba(31,20,9,0.08)',
            }}
          >
            <ChronicleFragment
              count={signals.uploads}
              singular="moment gathered"
              plural="moments gathered"
            />
            <ChronicleFragment
              count={signals.marks}
              singular="mark left on the wall"
              plural="marks left on the wall"
            />
            <ChronicleFragment
              count={signals.words}
              singular="word drifted into the room"
              plural="words drifted into the room"
            />
          </ul>
        )}

        {/* No buttons. The card itself is the CTA; the page is the
            archival surface. Composition belongs to a future iter,
            and the brief is explicit: the Chronicle composer must
            never feel bolted on. */}
      </main>
    </div>
  );
};

export default FinalNightPage;
